/**
 * PaymentSheet Component
 *
 * A sliding sheet/drawer component that handles the complete payment flow for prescriptions.
 * This component integrates with:
 * - Stripe Connect for secure credit card processing
 * - Shipping service for carrier selection and cost calculation
 * - Prescription service for fetching prescription details
 *
 * Features:
 * - One-time and subscription payment options
 * - Shipping carrier and delivery option selection
 * - Free shipment detection and display (global or clinic-specific)
 * - Split payment handling (shipping goes to NovaMed, medication to clinic)
 *
 * @module PaymentSheet
 */

import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CreditCard,
  RefreshCw,
  Lock,
  AlertCircle,
  CheckCircle2,
  Package,
  ChevronDown,
  X,
  Calendar,
  Repeat,
  Truck,
  Gift,
  Pencil,
  Pill,
  ExternalLink,
  Loader2,
  Snowflake,
} from 'lucide-react';
import {
  BinInformation,
  FormState,
  Prescription,
  ShippingCarrier,
  ShippingOption,
  ShippingSelection,
} from '@/lib/types';
import { prescriptionService } from '@/services/prescriptionService';
import { useAuth } from '@/contexts/AuthContext';
import {
  processPayment,
  isStripeOnboardingRequiredError,
  STRIPE_ONBOARDING_TOAST,
} from '@/services/paymentService'
import { useToast } from '@/hooks/use-toast';
import {
  shippingService,
  prescriptionMedicationRequestsToShippingLines,
  type PackageDimensions,
} from '@/services/shippingService';
import { getStripeInstance } from '@/lib/stripe-loader'
import { StripePaymentForm } from '@/components/payments/StripePaymentForm'

import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate } from 'react-router-dom';
import { ToastAction } from '@/components/ui/toast';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import * as Accordion from '@radix-ui/react-accordion';
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import EditMedicationsDialog from './EditMedicationsDialog';

/**
 * Props for the PaymentSheet component
 */
interface PaymentSheetProps {
  /** Whether the sheet is open/visible */
  open: boolean;
  /** Callback when sheet open state changes */
  onOpenChange: (open: boolean) => void;
  /** UUID of the prescription being paid for */
  prescriptionId: string;
  /** Callback when payment is successfully completed */
  onPaymentComplete: (paymentType: 'one-time' | 'subscription') => void;
  /** Optional callback when prescription is updated (e.g., medications removed) */
  onPrescriptionUpdated?: () => void;
}

/**
 * PaymentSheet component for processing prescription payments
 *
 * This component renders a full payment flow including:
 * 1. Prescription summary with medication details
 * 2. Shipping carrier and delivery option selection
 * 3. Payment type selection (one-time or subscription)
 * 4. Stripe-powered credit card form
 * 5. Payment summary with breakdown
 *
 * The component handles free shipment eligibility by checking if the patient's
 * state qualifies for free shipping (either globally or for the specific clinic).
 * When free shipment applies, all shipping options show $0 cost while preserving
 * the original price for display.
 *
 * @param {PaymentSheetProps} props - Component props
 * @returns {JSX.Element} The payment sheet component
 */
const PaymentSheet: React.FC<PaymentSheetProps> = ({
  open,
  onOpenChange,
  onPaymentComplete,
  prescriptionId,
  onPrescriptionUpdated,
}) => {
  const { user } = useAuth();
  const { clinic, isAdmin } = useClinic();
  const { toast } = useToast();
  const navigate = useNavigate();
  const stripeAccountId = clinic?.paymentAccount?.stripe_account_id

  // Payment amount state
  /** Total payment amount in cents (medication + shipping, after discount) */
  const [totalAmount, setTotalAmount] = useState(0);
  /** Medication-only amount in cents (excluding shipping, after discount) */
  const [medicationAmount, setMedicationAmount] = useState(0);
  /** Total discount in cents across all medication requests */
  const [totalDiscountCents, setTotalDiscountCents] = useState(0);
  /** Subtotal before discount in cents */
  const [subtotalBeforeDiscountCents, setSubtotalBeforeDiscountCents] = useState(0);

  // Prescription and form state
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState<'one-time' | 'subscription'>('one-time');
  const [isError, setIsError] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [show, setShow] = useState<string | null>(null);
  const [subscriptionFrequency, setSubscriptionFrequency] = useState<
    'weekly' | 'monthly' | 'quarterly'
  >('monthly');
  const [isEditMedicationsOpen, setIsEditMedicationsOpen] = useState(false);
  /** When true, show inline onboarding block (e.g. after API returned onboarding required) */
  const [showOnboardingInSheet, setShowOnboardingInSheet] = useState(false);
  /** Onboarding URL after "Generate new link"; used for "Open new link" */
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  // COMMENTED: Override option for price warning - payment blocked when selling price <= base price
  // const [priceWarningAcknowledged, setPriceWarningAcknowledged] = useState(false);

  // ============================================
  // SHIPPING STATE
  // ============================================
  /** Available shipping carriers (UPS, FedEx, Ameriship, etc.) */
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  /** Currently selected carrier ID */
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  /** Available delivery options for selected carrier */
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  /** Currently selected shipping option ID */
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string>('');
  /** Loading state for carriers fetch */
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  /** Loading state for shipping options fetch */
  const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
  /** Whether patient qualifies for free shipment */
  const [isFreeShipment, setIsFreeShipment] = useState(false);
  /** Type of free shipment: 'global' or 'clinic_specific' */
  const [freeShipmentType, setFreeShipmentType] = useState<string | null>(null);
  /** Patient's state for free shipment display */
  const [patientState, setPatientState] = useState<string | null>(null);
  /** Package dimensions used for shipping rate calculation */
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions | null>(null);
  /** Error message for shipping options (shown inline) */
  const [shippingError, setShippingError] = useState<string | null>(null);
  /** Whether this order requires cold chain (overnight/express) shipping */
  const [requiresColdChain, setRequiresColdChain] = useState(false);
  const patientOverviewPath = prescription?.patient?.id
    ? isAdmin
      ? `/patients/${prescription.patient.id}`
      : `/practitioner-portal/patients/${prescription.patient.id}`
    : null;
  const shouldShowAddressLink = shippingError?.includes(
    'No delivery options available for this address'
  );

  /**
   * Gets the current shipping selection object
   * @returns {ShippingSelection | null} The shipping selection or null if not fully selected
   */
  const getShippingSelection = (): ShippingSelection | null => {
    if (!selectedCarrierId || !selectedShippingOptionId) return null;

    const carrier = carriers.find(c => c.id === selectedCarrierId);
    const option = shippingOptions.find(o => o.id === selectedShippingOptionId);

    if (!carrier || !option) return null;

    return {
      carrierId: carrier.id,
      carrierName: carrier.name,
      optionId: option.id,
      optionName: option.name,
      cost: option.totalCost,
      originalCost: option.originalCost,
      isFreeShipment,
      freeShipmentType,
    };
  };

  const handleStripePayment = async (paymentMethodId: string) => {
    setIsProcessing(true)

    try {
      if (!prescription?.is_external_fulfillment && (!selectedCarrierId || !selectedShippingOptionId)) {
        toast({
          title: 'Shipping Required',
          description: 'Please select a shipping carrier and delivery option.',
          variant: 'destructive',
        })
        setIsProcessing(false)
        return
      }

      if (!clinic?.id) {
        toast({
          title: 'Payment Error',
          description: 'Clinic payment configuration is missing',
          variant: 'destructive',
        })
        setIsProcessing(false)
        return
      }

      const shippingSelection = getShippingSelection()
      const requestPayload = {
        prescription_id: prescriptionId,
        clinic_id: clinic.id,
        amount: totalAmount / 100,
        payment_method_id: paymentMethodId,
        is_subscription: paymentType === 'subscription',
        subscription_frequency: paymentType === 'subscription' ? subscriptionFrequency : undefined,
        subscription_auto_renew: paymentType === 'subscription' ? true : undefined,
        shipping: shippingSelection
          ? {
              carrier_id: shippingSelection.carrierId,
              carrier_name: shippingSelection.carrierName,
              service_id: shippingSelection.optionId,
              service_name: shippingSelection.optionName,
              cost_cents: Math.round(shippingSelection.cost * 100),
              original_cost_cents: Math.round(shippingSelection.originalCost * 100),
              is_free_shipment: shippingSelection.isFreeShipment,
              free_shipment_type: shippingSelection.freeShipmentType,
            }
          : undefined,
      }

      const result = await processPayment(requestPayload)

      if (result.success) {
        setPaymentSuccess(true)

        const successMessage =
          paymentType === 'subscription'
            ? `Subscription created! Payment of $${(totalAmount / 100).toFixed(2)} processed. Your prescription will automatically refill ${getFrequencyLabel(subscriptionFrequency).toLowerCase()}.`
            : `Payment of $${(totalAmount / 100).toFixed(2)} processed successfully.`

        toast({
          title: 'Payment Successful! 🎉',
          description: successMessage,
          duration: 5000,
        })

        setTimeout(() => {
          onPaymentComplete(paymentType)
          setPaymentSuccess(false)
          onOpenChange(false)
        }, 3000)
      } else if (result.error?.requires_action && result.error?.client_secret) {
        const connectedAccountId = clinic.paymentAccount?.stripe_account_id
        if (!connectedAccountId) {
          toast({
            title: 'Payment Failed',
            description: 'Stripe account is not configured for this clinic',
            variant: 'destructive',
          })
          return
        }

        const stripe = await getStripeInstance(connectedAccountId)
        if (!stripe) {
          toast({
            title: 'Payment Failed',
            description: 'Unable to initialize Stripe',
            variant: 'destructive',
          })
          return
        }

        const { error } = await stripe.handleNextAction({
          clientSecret: result.error.client_secret,
        })

        if (error) {
          toast({
            title: 'Authentication failed',
            description: error.message || '3D Secure authentication failed',
            variant: 'destructive',
          })
          return
        }

        const recheckResult = await processPayment(requestPayload)
        if (recheckResult.success) {
          setPaymentSuccess(true)

          const successMessage =
            paymentType === 'subscription'
              ? `Subscription created! Payment of $${(totalAmount / 100).toFixed(2)} processed. Your prescription will automatically refill ${getFrequencyLabel(subscriptionFrequency).toLowerCase()}.`
              : `Payment of $${(totalAmount / 100).toFixed(2)} processed successfully.`

          toast({
            title: 'Payment Successful! 🎉',
            description: successMessage,
            duration: 5000,
          })

          setTimeout(() => {
            onPaymentComplete(paymentType)
            setPaymentSuccess(false)
            onOpenChange(false)
          }, 3000)
        } else {
          const errMsg =
            recheckResult.error?.message || 'Payment could not be processed'
          if (isStripeOnboardingRequiredError({ message: errMsg })) {
            setShowOnboardingInSheet(true)
            toast({
              ...STRIPE_ONBOARDING_TOAST,
              variant: 'destructive',
              ...(isAdmin ? { action: (
                <ToastAction altText="Go to Billing" onClick={() => navigate('/payments?tab=settings')}>
                  Go to Billing
                </ToastAction>
              ) } : {}),
            })
          } else {
            toast({
              title: 'Payment Failed',
              description: errMsg,
              variant: 'destructive',
            })
          }
        }
      } else {
        const errMsg = result.error?.message || 'Payment could not be processed'
        if (isStripeOnboardingRequiredError({ message: errMsg })) {
          setShowOnboardingInSheet(true)
          toast({
            ...STRIPE_ONBOARDING_TOAST,
            variant: 'destructive',
            action: (
              <ToastAction altText="Go to Billing" onClick={() => navigate('/payments?tab=settings')}>
                Go to Billing
              </ToastAction>
            ),
          })
        } else {
          toast({
            title: 'Payment Failed',
            description: errMsg,
            variant: 'destructive',
          })
        }
      }
    } catch (error) {
      if (isStripeOnboardingRequiredError(error)) {
        setShowOnboardingInSheet(true)
        toast({
          ...STRIPE_ONBOARDING_TOAST,
          variant: 'destructive',
          action: (
            <ToastAction altText="Go to Billing" onClick={() => navigate('/payments?tab=settings')}>
              Go to Billing
            </ToastAction>
          ),
        })
      } else {
        // The API interceptor rejects with a plain ApiError object
        // ({ message, status, code, details }) — not an Error instance —
        // so an `instanceof Error` check drops the server's message and
        // shows a generic "Payment failed" string. Read `.message` off
        // either shape.
        const message =
          (error as { message?: string })?.message ||
          (error instanceof Error ? error.message : '') ||
          'Payment failed'
        toast({
          title: 'Payment Error',
          description: message,
          variant: 'destructive',
        })
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const getPrice = () => {
    return totalAmount / 100;
  };

  const getPriceDescription = () => {
    if (paymentType === 'one-time') {
      return 'One-time payment for prescription fulfillment';
    }

    const frequencyText =
      subscriptionFrequency === 'weekly'
        ? 'weekly'
        : subscriptionFrequency === 'monthly'
          ? 'monthly'
          : 'every 3 months';

    return `Recurring ${frequencyText} subscription - automatic refills`;
  };

  const hasSubscriptionEligibleMedications = prescription?.medicationRequests?.some(
    (mr: any) => mr.clinicProgram?.id || mr.clinic_program_id
  ) ?? false;

  const getFrequencyLabel = (frequency: 'weekly' | 'monthly' | 'quarterly') => {
    switch (frequency) {
      case 'weekly':
        return 'Every week (7 days)';
      case 'monthly':
        return 'Every month (30 days)';
      case 'quarterly':
        return 'Every 3 months (90 days)';
      default:
        return frequency;
    }
  };

  const handleGenerateOnboardingLink = async () => {
    if (!clinic?.id || isGeneratingLink) return
    setIsGeneratingLink(true)
    setOnboardingUrl(null)
    try {
      let result = await clinicPaymentService.refreshStripeOnboardingLink(clinic.id)
      if (!result?.onboardingUrl) {
        result = await clinicPaymentService.startStripeOnboarding(clinic.id)
      }
      if (result?.onboardingUrl) {
        setOnboardingUrl(result.onboardingUrl)
        window.open(result.onboardingUrl, '_blank')
      } else {
        toast({
          title: 'Could not get onboarding link',
          description: 'Please try again or complete setup in Settings → Payments.',
          variant: 'destructive',
        })
      }
    } catch {
      toast({
        title: 'Could not get onboarding link',
        description: 'Please try again or complete setup in Settings → Payments.',
        variant: 'destructive',
      })
    } finally {
      setIsGeneratingLink(false)
    }
  }

  const handleOpenOnboardingLink = () => {
    if (onboardingUrl) window.open(onboardingUrl, '_blank')
  }

  const fetchPrescription = async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      const res = await prescriptionService.getPrescriptionById(clinic?.id, prescriptionId);
      setPrescription(res.data.prescription);
      setMedicationAmount(res.data.total_amount);
      setTotalAmount(res.data.total_amount);
      setTotalDiscountCents(res.data.total_discount_cents || 0);
      setSubtotalBeforeDiscountCents(res.data.subtotal_before_discount || res.data.total_amount);
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Fetches available shipping carriers from the API
   */
  const fetchCarriers = async () => {
    if (!clinic?.id) return;

    setIsLoadingCarriers(true);
    try {
      const result = await shippingService.getCarriers(clinic.id);
      if (result.success && result.data) {
        setCarriers(result.data);
      } else {
        console.error('Failed to fetch carriers:', result.error);
        toast({
          title: 'Error',
          description: result.error || 'Failed to load shipping carriers',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  /**
   * Handles carrier selection and fetches shipping options for the selected carrier
   * @param {string} carrierId - The selected carrier ID
   */
  const handleCarrierChange = async (carrierId: string) => {
    setSelectedCarrierId(carrierId);
    setSelectedShippingOptionId('');
    setShippingOptions([]);
    setIsFreeShipment(false);
    setFreeShipmentType(null);
    setShippingError(null);
    setPackageDimensions(null);
    setRequiresColdChain(false);

    // Reset total to medication amount only
    setTotalAmount(medicationAmount);

    if (!carrierId || !clinic?.id || !prescription?.patient?.id) return;

    setIsLoadingShippingOptions(true);
    try {
      const result = await shippingService.calculateShippingCost(
        clinic.id,
        prescription.patient.id,
        carrierId,
        prescriptionMedicationRequestsToShippingLines(
          prescription.medicationRequests || []
        )
      );

      if (result.success && result.data) {
        setShippingOptions(result.data);
        setIsFreeShipment(result.isFreeShipment);
        setFreeShipmentType(result.freeShipmentType || null);
        setPatientState(result.patientState || null);
        setPackageDimensions(result.packageDimensions ?? null);
        setRequiresColdChain(result.requiresColdChain);
        setShippingError(null);

        // Auto-select first option if available
        if (result.data.length > 0) {
          handleShippingOptionChange(result.data[0].id, result.data);
        }
      } else {
        // Show error inline instead of toast
        setShippingError(
          result.error || 'Failed to calculate shipping cost. Please try a different carrier.'
        );
      }
    } catch (error) {
      console.error('Error calculating shipping cost:', error);
      setShippingError('Failed to load shipping options. Please try again.');
    } finally {
      setIsLoadingShippingOptions(false);
    }
  };

  /**
   * Handles shipping option selection and updates the total amount
   * @param {string} optionId - The selected shipping option ID
   * @param {ShippingOption[]} options - Optional shipping options array (used for auto-selection)
   */
  const handleShippingOptionChange = (optionId: string, options?: ShippingOption[]) => {
    setSelectedShippingOptionId(optionId);

    const optionsToSearch = options || shippingOptions;
    const selectedOption = optionsToSearch.find(o => o.id === optionId);

    if (selectedOption) {
      // Add shipping cost to medication amount (convert dollars to cents)
      const shippingCents = Math.round(selectedOption.totalCost * 100);
      setTotalAmount(medicationAmount + shippingCents);
    } else {
      setTotalAmount(medicationAmount);
    }
  };


  useEffect(() => {
    if (open) {
      fetchPrescription();
      fetchCarriers();
      setIsError(false);
      setErrorMessages([]);
      setPaymentSuccess(false);
      // setPriceWarningAcknowledged(false);
      // Reset shipping state
      setSelectedCarrierId('');
      setSelectedShippingOptionId('');
      setShippingOptions([]);
      setIsFreeShipment(false);
      setFreeShipmentType(null);
      setPatientState(null);
      setPackageDimensions(null);
      setShippingError(null);
      setRequiresColdChain(false);
    }
    return () => {};
  }, [prescriptionId, open]);

  useEffect(() => {
    if (!open) {
      setShowOnboardingInSheet(false);
      setOnboardingUrl(null);
    }
  }, [open]);

  useEffect(() => {
    if (paymentType === 'subscription' && !hasSubscriptionEligibleMedications) {
      setPaymentType('one-time');
    }
  }, [hasSubscriptionEligibleMedications]);

  useEffect(() => {
    if (prescription) {
      let hasError = false;
      let errorMsg = '';

      let medicationRequests = prescription?.medicationRequests || [];
      const missingProducts = medicationRequests.filter(
        medicationRequest => !medicationRequest.product_id
      );

      if (missingProducts.length > 0) {
        hasError = true;
        errorMsg = 'Please add a product to the Medication to continue';
        setIsError(true);
        setErrorMessages([errorMsg]);
        return;
      }

      for (let medicationRequest of medicationRequests) {
        let productDescription = medicationRequest?.product?.description;
        let basePrice =
          medicationRequest?.clinicProduct?.base_price_cents ||
          (medicationRequest?.product?.price_per_unit || medicationRequest?.product?.price_plan_cost || 0) * 100 ||
          0;
        let sellingPrice = medicationRequest?.clinicProduct?.selling_price_cents || basePrice || 0;
        let errorMessage = '';

        if (!basePrice || basePrice <= 0) {
          hasError = true;
          errorMessage = `${productDescription} does not have pricing set. Please configure base and selling prices before proceeding.`;
          setIsError(true);
          setErrorMessages(prev => [...prev, errorMessage]);
          continue;
        }

        if (!sellingPrice || sellingPrice <= 0) {
          hasError = true;
          errorMessage = `${productDescription} does not have pricing set. Please configure base and selling prices before proceeding.`;
          setIsError(true);
          setErrorMessages(prev => [...prev, errorMessage]);
          continue;
        }

        // Block payment when selling price <= base price (clinic has no margin or loses money)
        if (sellingPrice <= basePrice) {
          hasError = true;
          errorMessage = `${productDescription} has a patient price that is less than or equal to the pharmacy cost. Please configure a greater patient price than the pharmacy cost before processing the payment.`;
          setIsError(true);
          setErrorMessages(prev => [...prev, errorMessage]);
          continue;
        }
      }
    }
    return () => {
      setErrorMessages([]);
      setIsError(false);
    };
  }, [prescription]);

  // Detect when patient price is lower than or equal to pharmacy cost (clinic loses money or has no margin)
  const hasPriceWarning = (() => {
    const medicationRequests = prescription?.medicationRequests || [];
    for (const mr of medicationRequests) {
      const qty = Number(mr.quantity_authorized) || 1;
      const basePriceCents =
        mr.clinicProduct?.base_price_cents ??
        Math.round((mr.product?.price_per_unit || mr.product?.price_plan_cost || 0) * 100);
      const sellingPricePerUnit = mr.clinicProduct?.selling_price_cents ?? basePriceCents;
      const baseTotal = basePriceCents * qty;
      const sellingTotal =
        mr.selling_price_cents != null ? mr.selling_price_cents : sellingPricePerUnit * qty;
      if (baseTotal > 0 && sellingTotal <= baseTotal) return true;
    }
    return false;
  })();


  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <div className='flex items-center space-x-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10'>
                <CreditCard className='h-5 w-5 text-brand-teal' />
              </div>
              <div>
                <SheetTitle
                  className='font-semibold text-slate-900'
                  style={{ fontSize: '1.05rem' }}
                >
                  Process Payment
                </SheetTitle>
                <p className='text-sm text-slate-400'>Loading...</p>
              </div>
            </div>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>
          <div className='flex flex-1 items-center justify-center'>
            <RefreshCw className='h-6 w-6 animate-spin text-brand-teal' />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <div className='flex items-center space-x-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10'>
              <CreditCard className='h-5 w-5 text-brand-teal' />
            </div>
            <div>
              <SheetTitle className='font-semibold text-slate-900' style={{ fontSize: '1.05rem' }}>
                Process Payment
              </SheetTitle>
              <p className='text-sm text-brand-teal'>
                {prescription?.id?.slice(0, 6)} - {prescription?.patient?.full_name || 'Patient'}
              </p>
            </div>
          </div>
          <SheetClose asChild>
            <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-6 py-6'>
          {/* Success Overlay */}
          {paymentSuccess && (
            <div className='flex h-full items-center justify-center'>
              <div className='space-y-4 p-8 text-center'>
                <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
                  <CheckCircle2 className='h-8 w-8 text-green-600' />
                </div>
                <h3 className='text-2xl font-semibold text-slate-900'>
                  {paymentType === 'subscription' ? 'Subscription Created!' : 'Payment Successful!'}
                </h3>
                <p className='max-w-sm text-slate-600'>
                  {paymentType === 'subscription'
                    ? `Your subscription payment of $${(totalAmount / 100).toFixed(2)} has been processed. Your prescription will automatically refill ${getFrequencyLabel(subscriptionFrequency).toLowerCase()}.`
                    : `Your payment of $${(totalAmount / 100).toFixed(2)} has been processed successfully.`}
                </p>
                <p className='text-sm text-slate-500'>
                  Prescription {prescription?.id.slice(0, 6)} for {prescription?.patient?.full_name}
                </p>
                {paymentType === 'subscription' && (
                  <div className='mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                    <div className='flex items-center gap-2 text-sm text-blue-800'>
                      <Repeat className='h-4 w-4' />
                      <span>Manage your subscription in the Subscriptions page</span>
                    </div>
                  </div>
                )}
                <div className='pt-4'>
                  <div className='inline-flex items-center space-x-2 text-sm text-slate-500'>
                    <RefreshCw className='h-4 w-4 animate-spin' />
                    <span>Closing in a moment...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!paymentSuccess && (
            <div className='space-y-8'>
              {/* Prescription Summary */}
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                    Prescription Summary
                  </h3>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => setIsEditMedicationsOpen(true)}
                    className='h-8 px-3 text-xs text-slate-600 hover:text-slate-900'
                  >
                    <Pencil className='mr-1 h-3.5 w-3.5' />
                    Edit
                  </Button>
                </div>
                <div className='space-y-4 rounded-lg border border-slate-200 p-4 bg-slate-50/50'>
                  {/* Patient & Practitioner - shown once */}
                  <div className='flex flex-wrap gap-x-6 gap-y-2'>
                    <div>
                      <span className='text-xs text-slate-500 uppercase tracking-wide'>Patient</span>
                      <p className='text-sm font-medium text-slate-900'>
                        {prescription?.patient?.full_name || '--'}
                      </p>
                    </div>
                    <div>
                      <span className='text-xs text-slate-500 uppercase tracking-wide'>Practitioner</span>
                      <p className='text-sm font-medium text-slate-900'>
                        {prescription?.practitioner?.full_name || '--'}
                      </p>
                    </div>
                  </div>

                  {/* Programs as cards */}
                  <div className='space-y-2'>
                    <span className='text-xs text-slate-500 uppercase tracking-wide'>Programs</span>
                    {prescription?.medicationRequests?.map((medicationRequest, index) => {
                      const programOrClinicProgram =
                        medicationRequest?.programTemplate?.program || medicationRequest?.clinicProgram;
                      const is_program = !!programOrClinicProgram?.id;
                      const title =
                        programOrClinicProgram?.title || medicationRequest.medication || '--';
                      const qty = Number(medicationRequest?.quantity_authorized) || 1;
                      const priceStr = medicationRequest?.clinicProduct?.selling_price_cents
                        ? ((medicationRequest?.clinicProduct?.selling_price_cents * qty) / 100).toFixed(2)
                        : '--';
                      const perUnit = medicationRequest?.clinicProduct?.selling_price_cents
                        ? (medicationRequest.clinicProduct.selling_price_cents / 100).toFixed(2)
                        : null;
                      const itemDiscountCents = Number(medicationRequest?.discount_cents) || 0;

                      if (is_program) {
                        const program = programOrClinicProgram;
                        return (
                          <div
                            key={medicationRequest?.id}
                            className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'
                          >
                            <div className='flex items-start justify-between gap-3'>
                              <div className='flex min-w-0 flex-1 items-start gap-3'>
                                <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#60378B]/10'>
                                  <Package className='h-4 w-4 text-[#60378B]' />
                                </div>
                                <div className='min-w-0'>
                                  <p className='text-sm font-semibold text-slate-900'>{title}</p>
                                  <div className='mt-1 flex items-center gap-2'>
                                    <span className='inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'>
                                      Qty: {qty}
                                    </span>
                                    {qty > 1 && perUnit && (
                                      <span className='text-xs text-slate-500'>
                                        ${perUnit} × {qty}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className='shrink-0 text-right'>
                                <span className='text-base font-semibold text-emerald-600'>
                                  {priceStr !== '--' ? `$${priceStr}` : '--'}
                                </span>
                                {itemDiscountCents > 0 && (
                                  <p className='text-xs font-medium text-green-600'>
                                    -${(itemDiscountCents / 100).toFixed(2)} discount
                                  </p>
                                )}
                              </div>
                            </div>
                            {program && (program.subtitle || program.inclusions || program.target) && (
                              <Accordion.Root
                                type='single'
                                collapsible
                                value={show}
                                onValueChange={setShow}
                                className='mt-2'
                              >
                                <Accordion.Item value={medicationRequest?.id} className='border-0'>
                                  <Accordion.Trigger className='py-1 text-xs text-slate-500 hover:text-slate-700 hover:no-underline'>
                                    View program details
                                  </Accordion.Trigger>
                                  <Accordion.Content className='pb-0 pt-1 text-xs text-slate-600'>
                                    <div className='space-y-1 rounded bg-slate-50 p-2'>
                                      {program?.subtitle && <p><span className='text-slate-500'>Subtitle:</span> {program.subtitle}</p>}
                                      {program?.inclusions && <p><span className='text-slate-500'>Inclusions:</span> {program.inclusions}</p>}
                                      {program?.target && <p><span className='text-slate-500'>Target:</span> {program.target}</p>}
                                    </div>
                                  </Accordion.Content>
                                </Accordion.Item>
                              </Accordion.Root>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div
                          key={index}
                          className='flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm'
                        >
                          <div className='flex min-w-0 flex-1 items-center gap-3'>
                            <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                              <Pill className='h-4 w-4 text-slate-600' />
                            </div>
                            <div className='min-w-0'>
                              <p className='text-sm font-semibold text-slate-900'>{title}</p>
                              <div className='mt-0.5 flex items-center gap-2'>
                                <span className='inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'>
                                  Qty: {qty}
                                </span>
                                {qty > 1 && perUnit && (
                                  <span className='text-xs text-slate-500'>
                                    ${perUnit} × {qty}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className='shrink-0 text-right'>
                            <span className='text-base font-semibold text-emerald-600'>
                              {priceStr !== '--' ? `$${priceStr}` : '--'}
                            </span>
                            {itemDiscountCents > 0 && (
                              <p className='text-xs font-medium text-green-600'>
                                -${(itemDiscountCents / 100).toFixed(2)} discount
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Shipping Selection — hidden for external fulfillment (e.g. OptioRx) */}
              {!prescription?.is_external_fulfillment && (
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Shipping Options
                </h3>

                {/* Free Shipment Banner */}
                {isFreeShipment && patientState && (
                  <div className='flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3'>
                    <Gift className='h-5 w-5 text-green-600' />
                    <div>
                      <p className='text-sm font-medium text-green-800'>Free Shipping Available!</p>
                      <p className='text-xs text-green-600'>
                        Patient qualifies for free shipping to {patientState} (
                        {freeShipmentType === 'global' ? 'All Clinics' : 'Clinic Specific'})
                      </p>
                    </div>
                  </div>
                )}

                <div className='space-y-4 rounded-lg border border-slate-200 p-4'>
                  {/* Carrier Selection */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium text-slate-700'>
                      Shipping Carrier <span className='text-red-500'>*</span>
                    </Label>
                    <Select
                      value={selectedCarrierId}
                      onValueChange={handleCarrierChange}
                      disabled={isLoadingCarriers}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue
                          placeholder={
                            isLoadingCarriers ? 'Loading carriers...' : 'Select a carrier'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {carriers.map(carrier => (
                          <SelectItem key={carrier.id} value={carrier.id}>
                            <div className='flex items-center gap-2'>
                              <Truck className='h-4 w-4 text-slate-500' />
                              {carrier.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cold Chain Shipping Banner */}
                  {requiresColdChain && (
                    <div className='flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                      <Snowflake className='h-5 w-5 flex-shrink-0 text-blue-600' />
                      <div>
                        <p className='text-sm font-medium text-blue-800'>
                          Cold Chain Shipping Required
                        </p>
                        <p className='text-xs text-blue-600'>
                          This order contains temperature-sensitive medication. Only overnight/express options are shown.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Delivery Options */}
                  {selectedCarrierId && (
                    <div className='space-y-2'>
                      <Label className='text-sm font-medium text-slate-700'>
                        Delivery Option <span className='text-red-500'>*</span>
                      </Label>
                      {isLoadingShippingOptions ? (
                        <div className='flex items-center justify-center py-4'>
                          <RefreshCw className='h-5 w-5 animate-spin text-brand-teal' />
                          <span className='ml-2 text-sm text-slate-500'>Loading options...</span>
                        </div>
                      ) : shippingOptions.length > 0 ? (
                        <RadioGroup
                          value={selectedShippingOptionId}
                          onValueChange={value => handleShippingOptionChange(value)}
                          className='space-y-2'
                        >
                          {shippingOptions.map(option => (
                            <div
                              key={option.id}
                              className={cx(
                                'flex cursor-pointer items-center space-x-3 rounded-lg border p-3 transition-colors',
                                selectedShippingOptionId === option.id
                                  ? 'border-brand-teal bg-brand-teal/5'
                                  : 'border-slate-200 hover:bg-slate-50'
                              )}
                            >
                              <RadioGroupItem value={option.id} id={`shipping-${option.id}`} />
                              <Label
                                htmlFor={`shipping-${option.id}`}
                                className='flex flex-1 cursor-pointer items-center justify-between'
                              >
                                <span className='text-sm font-medium text-slate-900'>
                                  {option.name}
                                </span>
                                <div className='flex items-center gap-2'>
                                  {isFreeShipment && option.totalCost === 0 ? (
                                    <>
                                      <span className='text-sm text-slate-400 line-through'>
                                        ${option.originalCost.toFixed(2)}
                                      </span>
                                      <Badge
                                        variant='secondary'
                                        className='bg-green-100 text-green-700 hover:bg-green-100'
                                      >
                                        FREE
                                      </Badge>
                                    </>
                                  ) : (
                                    <span className='text-sm font-semibold text-slate-900'>
                                      ${option.totalCost.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      ) : shippingError ? (
                        <div className='flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 p-3'>
                          <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-red-600' />
                          <div className='flex flex-col gap-1'>
                            <span className='text-sm text-red-700'>{shippingError}</span>
                            {shouldShowAddressLink && patientOverviewPath && (
                              <a
                                href={patientOverviewPath}
                                className='text-sm font-medium text-red-700 underline hover:text-red-800'
                              >
                                View patient address
                              </a>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className='py-2 text-sm text-slate-500'>
                          No delivery options available for this carrier
                        </p>
                      )}
                    </div>
                  )}

                  {/* Package Dimensions */}
                  {packageDimensions && (
                    <div className='flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3'>
                      <Package className='h-4 w-4 flex-shrink-0 text-slate-500' />
                      <span className='text-sm text-slate-600'>
                        Package: {packageDimensions.length}" × {packageDimensions.width}" × {packageDimensions.height}"
                      </span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Payment Type Selection */}
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Choose Payment Type
                </h3>
                <RadioGroup
                  value={paymentType}
                  onValueChange={(value: 'one-time' | 'subscription') => setPaymentType(value)}
                >
                  <div className='space-y-3'>
                    <div className='flex items-center space-x-3 rounded-lg border border-slate-200 p-4 transition-colors hover:bg-slate-50'>
                      <RadioGroupItem value='one-time' id='one-time' />
                      <Label htmlFor='one-time' className='flex-1 cursor-pointer'>
                        <div className='flex items-center justify-between'>
                          <div>
                            <div className='flex items-center gap-2 text-sm font-medium text-slate-900'>
                              <CreditCard className='h-4 w-4' />
                              One-time Payment
                            </div>
                            <div className='text-xs text-slate-500'>
                              Pay once for this prescription
                            </div>
                          </div>
                          <div className='text-right'>
                            <div className='font-semibold text-slate-900'>
                              ${(totalAmount / 100).toFixed(2)}
                            </div>
                            <div className='text-xs text-slate-500'>One-time</div>
                          </div>
                        </div>
                      </Label>
                    </div>

                    <div className={`flex items-start space-x-3 rounded-lg border border-slate-200 p-4 transition-colors ${hasSubscriptionEligibleMedications ? 'hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`}>
                      <RadioGroupItem value='subscription' id='subscription' disabled={!hasSubscriptionEligibleMedications} />
                      <Label htmlFor='subscription' className={`flex-1 ${hasSubscriptionEligibleMedications ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                        <div className='flex flex-col gap-3'>
                          <div className='flex items-center justify-between'>
                            <div>
                              <div className='flex items-center gap-2 text-sm font-medium text-slate-900'>
                                <Repeat className='h-4 w-4' />
                                Subscription Payment
                              </div>
                              <div className='text-xs text-slate-500'>
                                {hasSubscriptionEligibleMedications
                                  ? 'Automatic recurring refills'
                                  : 'Not available — medications must be linked to a program'}
                              </div>
                            </div>
                            <div className='text-right'>
                              <div className='font-semibold text-slate-900'>
                                ${(totalAmount / 100).toFixed(2)}
                              </div>
                              <div className='text-xs text-slate-500'>Per refill</div>
                            </div>
                          </div>

                          {paymentType === 'subscription' && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className='border-t border-slate-200 pt-2'
                            >
                              <div className='mb-2 text-sm font-medium text-slate-700'>
                                Delivery Frequency:
                              </div>
                              <Select
                                value={subscriptionFrequency}
                                onValueChange={(value: 'weekly' | 'monthly' | 'quarterly') =>
                                  setSubscriptionFrequency(value)
                                }
                              >
                                <SelectTrigger className='w-full'>
                                  <SelectValue placeholder='Select frequency' />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value='weekly'>
                                    {getFrequencyLabel('weekly')}
                                  </SelectItem>
                                  <SelectItem value='monthly'>
                                    {getFrequencyLabel('monthly')}
                                  </SelectItem>
                                  <SelectItem value='quarterly'>
                                    {getFrequencyLabel('quarterly')}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <div className='mt-2 flex items-center gap-1 text-xs text-slate-500'>
                                <Calendar className='h-3 w-3' />
                                Next refill will be scheduled automatically
                              </div>
                            </motion.div>
                          )}
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Summary - shown before payment information */}
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Payment Summary
                </h3>
                <div className='space-y-3 rounded-lg border border-slate-200 p-4'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-500'>Order fulfillment:</span>
                    <span className='text-slate-900'>
                      ${(subtotalBeforeDiscountCents / 100).toFixed(2)}
                    </span>
                  </div>
                  {totalDiscountCents > 0 && (
                    <div className='flex justify-between text-sm'>
                      <span className='text-slate-500'>Discount:</span>
                      <span className='font-medium text-green-600'>
                        -${(totalDiscountCents / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-500'>Shipping:</span>
                    {selectedShippingOptionId ? (
                      <div className='flex items-center gap-2'>
                        {isFreeShipment ? (
                          <>
                            <span className='text-xs text-slate-400 line-through'>
                              $
                              {(
                                shippingOptions.find(o => o.id === selectedShippingOptionId)
                                  ?.originalCost || 0
                              ).toFixed(2)}
                            </span>
                            <span className='font-medium text-green-600'>$0.00</span>
                          </>
                        ) : (
                          <span className='text-slate-900'>
                            $
                            {(
                              shippingOptions.find(o => o.id === selectedShippingOptionId)
                                ?.totalCost || 0
                            ).toFixed(2)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className='text-xs text-slate-400'>{prescription?.is_external_fulfillment ? 'Not Applicable' : 'Select shipping'}</span>
                    )}
                  </div>
                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-500'>Processing fee:</span>
                    <span className='text-slate-900'>$0.00</span>
                  </div>
                  <Separator />
                  <div className='flex justify-between font-semibold'>
                    <span className='text-slate-900'>Total:</span>
                    <span className='text-slate-900'>${getPrice().toFixed(2)}</span>
                  </div>
                  <p className='text-xs text-slate-500'>{getPriceDescription()}</p>
                </div>
              </div>

              {/* Payment Information */}
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Payment Information
                </h3>
                <div className='rounded-lg border border-slate-200 p-4'>
                  {stripeAccountId && !showOnboardingInSheet ? (
                      <StripePaymentForm
                        connectedAccountId={stripeAccountId}
                        amountCents={Math.round(totalAmount)}
                        onPaymentMethodCreated={handleStripePayment}
                        onError={(err: string) =>
                          toast({ title: 'Payment Error', description: err, variant: 'destructive' })
                        }
                        isProcessing={isProcessing}
                        disabled={
                          errorMessages.length > 0 ||
                          (!prescription?.is_external_fulfillment && (!selectedCarrierId || !selectedShippingOptionId))
                        }
                      />
                    ) : (
                      <Card className='border border-amber-200 bg-amber-50/50'>
                        <CardContent className='p-4 space-y-4'>
                          <p className='text-sm text-slate-700'>
                            {STRIPE_ONBOARDING_TOAST.description}
                          </p>
                          {isAdmin && (
                            <div className='flex flex-wrap gap-2'>
                              <Button
                                type='button'
                                variant='default'
                                size='sm'
                                onClick={handleGenerateOnboardingLink}
                                disabled={!clinic?.id || isGeneratingLink}
                              >
                                {isGeneratingLink ? (
                                  <>
                                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                                    Generating…
                                  </>
                                ) : (
                                  <>
                                    <ExternalLink className='h-4 w-4 mr-2' />
                                    Complete onboarding
                                  </>
                                )}
                              </Button>
                              {onboardingUrl && (
                                <Button
                                  type='button'
                                  variant='outline'
                                  size='sm'
                                  onClick={handleOpenOnboardingLink}
                                >
                                  Open new link
                                </Button>
                              )}
                            </div>
                          )}
                          <p className='text-xs text-slate-500'>
                            {isAdmin
                              ? 'Opens Stripe in a new tab. After finishing, return here and try payment again.'
                              : 'Please contact your clinic admin to complete Stripe onboarding.'}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                </div>
              </div>


              {/* Security Notice */}
              <div className='flex items-center space-x-2 rounded-lg border border-green-100 bg-green-50 p-3'>
                <Lock className='h-4 w-4 text-green-600' />
                <span className='text-sm text-green-700'>
                  Your payment information is encrypted and secure
                </span>
              </div>

              {/* Payment Requirements - Show what's missing before payment can proceed */}
              {(() => {
                const requirements: { message: string; isMet: boolean }[] = [
                  ...(!prescription?.is_external_fulfillment ? [{
                    message: 'Select a shipping carrier and delivery option',
                    isMet: !!(selectedCarrierId && selectedShippingOptionId),
                  }] : []),
                  {
                    message: 'Complete the payment card details',
                    isMet: isFormValid,
                  },
                ];
                
                const unmetRequirements = requirements.filter(r => !r.isMet);
                
                if (unmetRequirements.length > 0 && !isError) {
                  return (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm font-medium text-amber-800">
                          Complete the following to proceed with payment:
                        </span>
                      </div>
                      <ul className="ml-6 space-y-1">
                        {unmetRequirements.map((req, index) => (
                          <li key={index} className="text-sm text-amber-700 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                            {req.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Error Messages */}
              {isError && (
                <div className='space-y-2'>
                  {errorMessages.map((errorMessage, index) => (
                    <div
                      key={index}
                      className='flex items-start space-x-2 rounded-lg border border-red-100 bg-red-50 p-3'
                    >
                      <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-red-600' />
                      <span className='text-sm text-red-700'>{errorMessage}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* COMMENTED: "I understand and want to proceed with payment" override - payment blocked when selling price <= base price */}
              {/* {hasPriceWarning && (
                <div className='flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                    <span className='text-sm text-amber-800'>
                      One or more medications have a patient price less than or equal to the pharmacy cost. The clinic may lose money or have no margin on this order. Please review pricing in Settings → Products before proceeding.
                    </span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <Checkbox
                      id='price-warning-ack'
                      checked={priceWarningAcknowledged}
                      onCheckedChange={(checked) => setPriceWarningAcknowledged(!!checked)}
                    />
                    <Label
                      htmlFor='price-warning-ack'
                      className='text-sm font-medium text-amber-800 cursor-pointer'
                    >
                      I understand and want to proceed with payment
                    </Label>
                  </div>
                </div>
              )} */}

              {/* Shipping required message */}
              {!prescription?.is_external_fulfillment && (!selectedCarrierId || !selectedShippingOptionId) && (
                <div className='flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3'>
                  <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                  <span className='text-sm text-amber-700'>
                    Please select a shipping carrier and delivery option to proceed with payment.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

      </SheetContent>

      {/* Edit Medications Dialog */}
      {prescription && (
        <EditMedicationsDialog
          open={isEditMedicationsOpen}
          onOpenChange={setIsEditMedicationsOpen}
          prescription={prescription}
          onMedicationsUpdated={() => {
            fetchPrescription(true); // Silent refetch - don't show loading state
            onPrescriptionUpdated?.(); // Notify parent to refetch list
          }}
        />
      )}
    </Sheet>
  );
};

export default PaymentSheet;
